import { zodResolver } from '@hookform/resolvers/zod';
import {
  MyExamCenterSearchSelection,
  ProvinceSearchSelection,
} from '@report/exam-center-report-management';
import { useGetMyExamCentersQuery, useGetAllProvincesQuery } from '@report/graphql';
import {
  Form,
  Flex,
  Text,
  Box,
  FormSearchMultipleSelection,
  Divider,
  Button,
  FormDateInput,
} from '@shared/components';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { EXAM_TYPE_OPTIONS, REGISTRATION_TYPE_OPTIONS } from '../../constants';
import { useExamEventOverviewReportSearchParams } from '../../hooks';

import {
  type ExamEventOverviewReportFilterFormInput,
  type ExamEventOverviewReportFilterFormOutput,
  ExamEventOverviewReportFilterSchema,
} from './schema';
import { mapFormValuesToSearchParams } from './utils';

export default function ExamEventOverviewReportFilterSection() {
  const [
    { startDate, endDate, registrationType, examType, examCenter, province },
    { changeFilterParams },
  ] = useExamEventOverviewReportSearchParams();

  const formContext = useForm<
    ExamEventOverviewReportFilterFormInput,
    unknown,
    ExamEventOverviewReportFilterFormOutput
  >({
    resolver: zodResolver(ExamEventOverviewReportFilterSchema),
    defaultValues: {
      examCenter: [],
      province: [],
      startDate: null,
      endDate: null,
      registrationType: [],
      examType: [],
    },
  });

  const onSubmit = (data: ExamEventOverviewReportFilterFormOutput) => {
    changeFilterParams(mapFormValuesToSearchParams(data));
  };

  const handleReset = () => {
    formContext.reset();
    changeFilterParams({
      examCenter: [],
      province: [],
      startDate: null,
      endDate: null,
      registrationType: [],
      examType: [],
    });
  };

  const selectedRegistrationTypeOption = useMemo(() => {
    return REGISTRATION_TYPE_OPTIONS.filter((option) => registrationType.includes(option.value));
  }, [registrationType]);

  const selectedExamTypeOption = useMemo(() => {
    return EXAM_TYPE_OPTIONS.filter((option) => examType.includes(option.value));
  }, [examType]);

  const { data: examCenterData } = useGetMyExamCentersQuery();

  const selectedExamCenterOption = useMemo(() => {
    const isSelectAllOption = examCenter.includes('all');
    if (isSelectAllOption) {
      return [
        {
          label: `Select All (${examCenterData?.reportGetMyExamCenters?.examCenters?.length ?? 0})`,
          value: 'all',
        },
      ];
    }
    return examCenterData?.reportGetMyExamCenters?.examCenters
      .filter((ec) => examCenter.includes(ec.code))
      .map((ec) => ({
        label: ec.code,
        value: ec.code,
      }));
  }, [examCenterData, examCenter]);

  const { data: provinceData } = useGetAllProvincesQuery();

  const selectedProvinceOption = useMemo(() => {
    const isSelectAllOption = province.includes('all');
    if (isSelectAllOption) {
      return [
        {
          label: `Select All (${provinceData?.reportGetAllProvinces?.provinces?.length ?? 0})`,
          value: 'all',
        },
      ];
    }
    return provinceData?.reportGetAllProvinces?.provinces
      .filter((p) => province.includes(p.id))
      .map((p) => ({ label: p.nameTh, value: p.id }));
  }, [provinceData, province]);

  useEffect(() => {
    formContext.reset({
      examCenter: selectedExamCenterOption,
      province: selectedProvinceOption,
      examType: selectedExamTypeOption,
      registrationType: selectedRegistrationTypeOption,
      startDate: startDate,
      endDate: endDate,
    });
  }, [
    selectedExamCenterOption,
    selectedProvinceOption,
    selectedRegistrationTypeOption,
    selectedExamTypeOption,
    startDate,
    endDate,
    formContext,
  ]);

  const watchedExamCenter = formContext.watch('examCenter');
  const watchedRegistrationType = formContext.watch('registrationType');
  const watchedExamType = formContext.watch('examType');
  const watchedProvince = formContext.watch('province');

  return (
    <FormProvider {...formContext}>
      <Form
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
        onSubmit={formContext.handleSubmit(onSubmit)}
      >
        <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Date Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                label='Start Date'
                name='startDate'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                placeholder='DD-MM-YYYY'
                yearLabelFormat='BBBB'
                fullWidth
              />
            </Box>
            <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                label='End Date'
                name='endDate'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                placeholder='DD-MM-YYYY'
                yearLabelFormat='BBBB'
                fullWidth
              />
            </Box>
          </Flex>
        </Flex>
        <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Other Filters:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <MyExamCenterSearchSelection
              name='examCenter'
              sx={{ flexBasis: '256px' }}
              placeholder={watchedExamCenter?.length === 0 ? 'เลือก Exam Center' : ''}
            />
            <FormSearchMultipleSelection
              sx={{ flexBasis: '256px' }}
              name='registrationType'
              label='Registration Type'
              placeholder={watchedRegistrationType.length === 0 ? 'เลือก Registration Type' : ''}
              options={REGISTRATION_TYPE_OPTIONS}
            />
            <FormSearchMultipleSelection
              sx={{ flexBasis: '256px' }}
              name='examType'
              label='Exam Type'
              placeholder={watchedExamType.length === 0 ? 'เลือก Exam Type' : ''}
              options={EXAM_TYPE_OPTIONS}
            />
            <ProvinceSearchSelection
              name='province'
              sx={{ flexBasis: '256px' }}
              placeholder={watchedProvince?.length === 0 ? 'เลือก Province' : ''}
            />
          </Flex>
        </Flex>
        <Divider />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button variant='outlined' color='secondary' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
